// Gestão de usuários pra admin — lista usuários da tabela profiles e
// gera SQL + senha temporária pronto pra colar no Supabase SQL Editor
// quando precisar resetar senha.
//
// Por que gerar SQL em vez de resetar direto: a operação de resetar
// senha de OUTRO usuário requer service_role key do Supabase, que não
// pode ficar no frontend por segurança. A UI ajuda o admin gerando
// tudo (senha aleatória forte + comando SQL pronto) — ele só cola.

import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../utils/supabase'
import { Search, KeyRound, Copy, Check, ShieldAlert, User as UserIcon, AlertCircle } from 'lucide-react'

// Gera senha forte legível: prefixo + ano + 2 sílabas + hexa
function gerarSenhaTemporaria(username) {
  const nomeCurto = (username || 'user').split('.')[0].slice(0, 8)
  const ano       = new Date().getFullYear()
  const hexa      = Math.floor(Math.random() * 0xfffff).toString(16).padStart(5, '0')
  return `${nomeCurto}_${ano}_${hexa}`
}

// SQL SÓ pra marcar must_change_password. A senha em si é trocada via
// Auth Dashboard (UPDATE auth.users é bloqueado pelo Supabase — permission
// denied). O admin abre Auth > Users > 3 pontos > "Update password".
function gerarSqlMustChange(username) {
  return `-- Força troca de senha no próximo login de ${username}
UPDATE profiles
SET must_change_password = true
WHERE username = '${username}';`
}

function emailDoUsuario(username) {
  return `${username}@minideck.local`
}

function badgeCor(role) {
  if (role === 'admin')     return 'bg-red-100 text-red-700 border-red-200'
  if (role === 'gerente')   return 'bg-amber-100 text-amber-700 border-amber-200'
  if (role === 'vendedora') return 'bg-blue-100 text-blue-700 border-blue-200'
  return 'bg-slate-100 text-slate-600 border-slate-200'
}

export function AdminUsuarios() {
  const { profile } = useAuth()
  const [usuarios, setUsuarios] = useState([])
  const [busca,    setBusca]    = useState('')
  const [erro,     setErro]     = useState(null)
  const [carregando, setCarregando] = useState(true)
  const [modalReset, setModalReset] = useState(null)  // { user, senha, sql }

  // Gate de segurança
  const ehAdmin = profile?.role === 'admin'

  useEffect(() => {
    if (!ehAdmin) { setCarregando(false); return }
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('username', { ascending: true })
      if (error) setErro(error.message)
      else setUsuarios(data || [])
      setCarregando(false)
    })()
  }, [ehAdmin])

  const filtrados = useMemo(() => {
    if (!busca.trim()) return usuarios
    const q = busca.toLowerCase().trim()
    return usuarios.filter(u =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.nome     || '').toLowerCase().includes(q) ||
      (u.role     || '').toLowerCase().includes(q)
    )
  }, [usuarios, busca])

  function abrirResetSenha(u) {
    const senha = gerarSenhaTemporaria(u.username)
    const email = emailDoUsuario(u.username)
    setModalReset({ user: u, senha, email, status: 'confirmar' })
  }

  async function executarReset() {
    if (!modalReset) return
    setModalReset(m => ({ ...m, status: 'processando' }))
    const { data, error } = await supabase.rpc('admin_reset_user_password', {
      target_username: modalReset.user.username,
      new_password:    modalReset.senha,
    })
    if (error) {
      setModalReset(m => ({ ...m, status: 'erro', erro: error.message }))
      return
    }
    if (data?.error) {
      setModalReset(m => ({ ...m, status: 'erro', erro: data.error }))
      return
    }
    setModalReset(m => ({ ...m, status: 'sucesso' }))
    // Atualiza a lista local pra mostrar o badge "troca senha no login"
    setUsuarios(prev => prev.map(u =>
      u.id === modalReset.user.id ? { ...u, must_change_password: true } : u
    ))
  }

  // Não é admin → bloqueia
  if (!carregando && !ehAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <ShieldAlert className="mx-auto text-red-500 mb-3" size={48} />
        <h1 className="text-xl font-bold text-slate-800 mb-2">Acesso restrito</h1>
        <p className="text-sm text-slate-500">
          Esta página é só pra usuários com <code className="bg-slate-100 px-1 rounded">role = admin</code>.
          Fale com um administrador do MINI Deck.
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-slate-800">Gestão de usuários</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          Reset de senha gera SQL pronto pra colar no Supabase SQL Editor —
          sem risco de expor chave admin no navegador.
        </p>
      </div>

      {/* Busca */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder="Buscar por usuário, nome ou role…"
          className="input pl-9 text-sm"
        />
      </div>

      {/* Erro de carregamento */}
      {erro && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{erro}</p>
        </div>
      )}

      {/* Lista */}
      {carregando ? (
        <p className="text-center text-sm text-slate-400 py-8">Carregando usuários…</p>
      ) : filtrados.length === 0 ? (
        <p className="text-center text-sm text-slate-400 py-8">
          {busca ? 'Nenhum usuário bate com a busca.' : 'Nenhum usuário na tabela profiles.'}
        </p>
      ) : (
        <div className="space-y-2">
          {filtrados.map(u => (
            <div key={u.id} className="card p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                <UserIcon size={16} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-slate-800">{u.username || '—'}</p>
                  {u.role && (
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${badgeCor(u.role)}`}>
                      {u.role}
                    </span>
                  )}
                  {u.must_change_password && (
                    <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">
                      Troca senha no login
                    </span>
                  )}
                </div>
                {u.nome && u.nome !== u.username && (
                  <p className="text-xs text-slate-500 mt-0.5">{u.nome}</p>
                )}
              </div>
              <button
                onClick={() => abrirResetSenha(u)}
                className="btn-secondary text-xs shrink-0"
                title="Gera SQL + senha temporária pra você colar no Supabase"
              >
                <KeyRound size={12} />
                Resetar senha
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal de reset */}
      {modalReset && (
        <ModalReset
          data={modalReset}
          onClose={() => setModalReset(null)}
          onReset={executarReset}
        />
      )}
    </div>
  )
}

function ModalReset({ data, onClose, onReset }) {
  const [copiadoSenha, setCopiadoSenha] = useState(false)
  const [copiadoMsg,   setCopiadoMsg]   = useState(false)

  async function copiar(texto, tipo) {
    try {
      await navigator.clipboard.writeText(texto)
      if (tipo === 'senha') { setCopiadoSenha(true); setTimeout(() => setCopiadoSenha(false), 2000) }
      if (tipo === 'msg')   { setCopiadoMsg(true);   setTimeout(() => setCopiadoMsg(false), 2000) }
    } catch { /* clipboard bloqueado */ }
  }

  const mensagemWhatsApp = `Sua senha temporária no MINI Deck é: ${data.senha}\nLogin: ${data.user.username}\n\nAo entrar o app vai pedir pra você criar uma nova senha — cria uma que só você saiba.`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={data.status === 'processando' ? undefined : onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">

        {/* CONFIRMAR — antes de executar */}
        {data.status === 'confirmar' && (
          <>
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800">Resetar senha de {data.user.username}?</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600 leading-relaxed">
                Vou gerar essa senha temporária e trocar direto no Supabase:
              </p>
              <div className="border-2 border-dashed border-[#F5C200] bg-amber-50/40 rounded-lg p-4 text-center">
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Senha temporária</p>
                <p className="font-mono text-xl font-black text-slate-800 break-all mt-1">{data.senha}</p>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                A pessoa vai entrar com essa senha e o app vai forçar ela a criar uma pessoal.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex gap-3">
              <button onClick={onClose} className="btn-secondary flex-1 justify-center">Cancelar</button>
              <button onClick={onReset} className="btn-primary flex-1 justify-center">
                Resetar agora
              </button>
            </div>
          </>
        )}

        {/* PROCESSANDO */}
        {data.status === 'processando' && (
          <div className="p-8 text-center">
            <div className="mx-auto w-10 h-10 border-4 border-[#F5C200] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm text-slate-600">Trocando senha no Supabase…</p>
          </div>
        )}

        {/* SUCESSO */}
        {data.status === 'sucesso' && (
          <>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Check size={18} className="text-emerald-600" />
              <h3 className="font-bold text-slate-800">Senha resetada com sucesso</h3>
            </div>
            <div className="p-5 space-y-4">
              <div className="border-2 border-dashed border-[#F5C200] rounded-lg p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Senha temporária</p>
                  <p className="font-mono text-lg font-black text-slate-800 break-all">{data.senha}</p>
                </div>
                <button
                  onClick={() => copiar(data.senha, 'senha')}
                  className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors
                    ${copiadoSenha ? 'bg-emerald-500 text-white' : 'bg-[#F5C200] hover:bg-[#e6b800] text-[#111111]'}`}
                >
                  {copiadoSenha ? <Check size={12} /> : <Copy size={12} />}
                  {copiadoSenha ? 'Copiado' : 'Copiar'}
                </button>
              </div>

              <div>
                <p className="text-xs font-bold text-slate-700 mb-2">Mensagem pronta pra WhatsApp:</p>
                <div className="relative">
                  <pre className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap font-sans text-slate-700 leading-relaxed">{mensagemWhatsApp}</pre>
                  <button
                    onClick={() => copiar(mensagemWhatsApp, 'msg')}
                    className={`absolute top-2 right-2 flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-colors
                      ${copiadoMsg ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'}`}
                  >
                    {copiadoMsg ? <Check size={11} /> : <Copy size={11} />}
                    {copiadoMsg ? 'Copiado' : 'Copiar mensagem'}
                  </button>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100">
              <button onClick={onClose} className="btn-primary w-full justify-center">Fechar</button>
            </div>
          </>
        )}

        {/* ERRO */}
        {data.status === 'erro' && (
          <>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <AlertCircle size={18} className="text-red-600" />
              <h3 className="font-bold text-slate-800">Não foi possível resetar</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-red-700 font-mono bg-red-50 border border-red-200 rounded p-3 break-all">
                {data.erro}
              </p>
              {data.erro === 'not_authorized' && (
                <p className="text-xs text-slate-600 leading-relaxed">
                  Seu profile.role não está como <code className="bg-slate-100 px-1 rounded">admin</code>.
                  Roda no SQL Editor: <code className="bg-slate-100 px-1 rounded">UPDATE profiles SET role = 'admin' WHERE username = '{data.user.username}'</code> e loga de novo.
                </p>
              )}
              {data.erro === 'user_not_found' && (
                <p className="text-xs text-slate-600">
                  Usuário não existe no Supabase Auth com o email <code className="bg-slate-100 px-1 rounded">{data.email}</code>.
                </p>
              )}
              {data.erro?.includes('function') && data.erro?.includes('does not exist') && (
                <p className="text-xs text-slate-600 leading-relaxed">
                  A função <code className="bg-slate-100 px-1 rounded">admin_reset_user_password</code> não foi criada ainda no Supabase.
                  Roda o SQL de setup que passei no chat.
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100">
              <button onClick={onClose} className="btn-secondary w-full justify-center">Fechar</button>
            </div>
          </>
        )}

      </div>
    </div>
  )
}
